import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { fmtDateTime } from './TasksCommon';
import './Tasks.css';

const TRIGGERS = [
  { value: 'task_created', label: 'Задача создана' },
  { value: 'task_status_changed', label: 'Изменён статус' },
  { value: 'task_priority_changed', label: 'Изменён приоритет' },
  { value: 'task_became_overdue', label: 'Задача просрочена (по крону)' },
  { value: 'task_stale', label: 'Задача не двигается N дней (по крону)' },
];

const ACTIONS = [
  { value: 'notify_user', label: 'Уведомить конкретного пользователя' },
  { value: 'notify_role', label: 'Уведомить по роли' },
  { value: 'assign_to_user', label: 'Назначить исполнителем' },
  { value: 'set_priority', label: 'Изменить приоритет' },
];

const ROLES = [
  { value: 'assignee', label: 'Исполнитель' },
  { value: 'reporter', label: 'Постановщик' },
  { value: 'lead', label: 'Лид команды' },
  { value: 'director', label: 'Директор / замы' },
];

const PRIORITIES = [
  { value: 'low', label: 'Низкий' },
  { value: 'medium', label: 'Средний' },
  { value: 'high', label: 'Высокий' },
  { value: 'critical', label: 'Критический' },
];

const STATUSES = [
  { value: 'new', label: 'Новая' },
  { value: 'in_progress', label: 'В работе' },
  { value: 'review', label: 'На проверке' },
  { value: 'done', label: 'Готово' },
  { value: 'cancelled', label: 'Отменена' },
];

const EMPTY_FORM = {
  name: '',
  is_active: true,
  trigger: 'task_created',
  action: 'notify_role',
  applies_to_project: '',
  condition: {},
  action_params: { role: 'lead', message: '' },
};

export default function AutomationsList() {
  const [rules, setRules] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [rRes, uRes, pRes] = await Promise.all([
        api.getAutomationRules(),
        api.getUsers(),
        api.getProjects(),
      ]);
      setRules(rRes.data.results || rRes.data);
      const usersData = uRes.data.results || uRes.data;
      setUsers(usersData.map((u) => ({
        id: u.id, username: u.username,
        full_name: [u.last_name, u.first_name].filter(Boolean).join(' '),
      })));
      setProjects(pRes.data.results || pRes.data);
    } catch (e) {
      alert('Не удалось загрузить: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditing('new');
  };
  const openEdit = (r) => {
    setForm({
      name: r.name,
      is_active: r.is_active,
      trigger: r.trigger,
      action: r.action,
      applies_to_project: r.applies_to_project || '',
      condition: r.condition || {},
      action_params: r.action_params || {},
    });
    setEditing(r.id);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        applies_to_project: form.applies_to_project || null,
      };
      if (editing === 'new') {
        await api.createAutomationRule(payload);
      } else {
        await api.updateAutomationRule(editing, payload);
      }
      setEditing(null);
      load();
    } catch (e) {
      alert('Не удалось сохранить: ' + (e.response?.data?.detail || JSON.stringify(e.response?.data) || e.message));
    }
    setSaving(false);
  };

  const handleToggle = async (id) => {
    try {
      await api.toggleAutomationRule(id);
      load();
    } catch { /* ignore */ }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить правило?')) return;
    try {
      await api.deleteAutomationRule(id);
      load();
    } catch (e) {
      alert('Не удалось удалить');
    }
  };

  return (
    <div className="tasks-page">
      <div style={{ marginBottom: 8 }}>
        <Link to="/tasks" style={{ color: '#6b7280', textDecoration: 'none', fontSize: 14 }}>← Дашборд задач</Link>
      </div>
      <h1>⚙ Автоматизации</h1>
      <p style={{ color: '#6b7280', marginTop: -10, maxWidth: 780 }}>
        Правила, которые срабатывают автоматически. Например: «Если задача просрочена больше 2 дней — уведомить лида команды»
        или «Если создана задача с приоритетом Критический — уведомить директора».
        Не нужно каждый раз проверять вручную — система делает это за вас.
      </p>

      <div className="tasks-toolbar">
        <button className="tasks-btn primary" onClick={openNew}>+ Новое правило</button>
      </div>

      {loading ? (
        <div className="tasks-empty">Загрузка…</div>
      ) : rules.length === 0 ? (
        <div className="tasks-empty">Правил пока нет</div>
      ) : (
        <table className="tasks-table">
          <thead>
            <tr>
              <th>Название</th>
              <th>Триггер</th>
              <th>Действие</th>
              <th>Проект</th>
              <th>Сработало</th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id} style={{ opacity: r.is_active ? 1 : 0.5 }}>
                <td>{r.name}</td>
                <td>{r.trigger_display}</td>
                <td>{r.action_display}</td>
                <td>{r.project_title || 'все'}</td>
                <td>
                  {r.trigger_count}
                  {r.last_triggered_at && <span style={{ color: '#9ca3af', fontSize: 12, marginLeft: 6 }}>{fmtDateTime(r.last_triggered_at)}</span>}
                </td>
                <td>
                  <button className="tasks-btn" onClick={() => handleToggle(r.id)}>
                    {r.is_active ? '🔴 Выкл' : '🟢 Вкл'}
                  </button>
                </td>
                <td>
                  <button className="tasks-btn" style={{ marginRight: 4 }} onClick={() => openEdit(r)}>Изменить</button>
                  <button className="tasks-btn danger" onClick={() => handleDelete(r.id)}>Удалить</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <div className="tasks-modal-overlay" onClick={() => setEditing(null)}>
          <div className="tasks-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setEditing(null)}>×</button>
            <h2>{editing === 'new' ? 'Новое правило' : 'Правило'}</h2>

            <div className="tasks-form-row">
              <label>Название*</label>
              <input className="tasks-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>

            <div className="tasks-form-row">
              <label>Активно</label>
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            </div>

            <div className="tasks-form-row">
              <label>Триггер</label>
              <select className="tasks-select" value={form.trigger} onChange={(e) => setForm({ ...form, trigger: e.target.value, condition: {} })}>
                {TRIGGERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            {/* Условие */}
            {form.trigger === 'task_status_changed' && (
              <div className="tasks-form-row">
                <label>Новый статус</label>
                <select className="tasks-select" value={form.condition.status_to || ''} onChange={(e) => setForm({ ...form, condition: { ...form.condition, status_to: e.target.value || undefined } })}>
                  <option value="">любой</option>
                  {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
            )}

            {(form.trigger === 'task_created' || form.trigger === 'task_priority_changed') && (
              <div className="tasks-form-row">
                <label>Приоритет</label>
                <select className="tasks-select" value={form.condition.priority || ''} onChange={(e) => setForm({ ...form, condition: { ...form.condition, priority: e.target.value || undefined } })}>
                  <option value="">любой</option>
                  {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            )}

            {form.trigger === 'task_became_overdue' && (
              <div className="tasks-form-row">
                <label>Просрочка ≥ дней</label>
                <input type="number" min="1" className="tasks-input" value={form.condition.overdue_days || 1} onChange={(e) => setForm({ ...form, condition: { ...form.condition, overdue_days: parseInt(e.target.value, 10) || 1 } })} />
              </div>
            )}

            {form.trigger === 'task_stale' && (
              <div className="tasks-form-row">
                <label>Застой ≥ дней</label>
                <input type="number" min="1" className="tasks-input" value={form.condition.days || 7} onChange={(e) => setForm({ ...form, condition: { ...form.condition, days: parseInt(e.target.value, 10) || 7 } })} />
              </div>
            )}

            <div className="tasks-form-row">
              <label>Действие</label>
              <select className="tasks-select" value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value, action_params: {} })}>
                {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>

            {/* Параметры действия */}
            {(form.action === 'notify_user' || form.action === 'assign_to_user') && (
              <div className="tasks-form-row">
                <label>Пользователь</label>
                <select className="tasks-select" value={form.action_params.user_id || ''} onChange={(e) => setForm({ ...form, action_params: { ...form.action_params, user_id: parseInt(e.target.value, 10) || undefined } })}>
                  <option value="">— выбрать —</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.full_name || u.username}</option>)}
                </select>
              </div>
            )}

            {form.action === 'notify_role' && (
              <div className="tasks-form-row">
                <label>Роль</label>
                <select className="tasks-select" value={form.action_params.role || 'lead'} onChange={(e) => setForm({ ...form, action_params: { ...form.action_params, role: e.target.value } })}>
                  {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
            )}

            {form.action === 'set_priority' && (
              <div className="tasks-form-row">
                <label>Новый приоритет</label>
                <select className="tasks-select" value={form.action_params.priority || 'high'} onChange={(e) => setForm({ ...form, action_params: { ...form.action_params, priority: e.target.value } })}>
                  {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            )}

            {(form.action === 'notify_user' || form.action === 'notify_role') && (
              <div className="tasks-form-row">
                <label>Текст сообщения</label>
                <textarea className="tasks-textarea" value={form.action_params.message || ''} onChange={(e) => setForm({ ...form, action_params: { ...form.action_params, message: e.target.value } })} />
              </div>
            )}

            <div className="tasks-form-row">
              <label>Проект (пусто — все)</label>
              <select className="tasks-select" value={form.applies_to_project} onChange={(e) => setForm({ ...form, applies_to_project: e.target.value })}>
                <option value="">все проекты</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </div>

            <div className="tasks-form-actions">
              <button className="tasks-btn" onClick={() => setEditing(null)}>Отмена</button>
              <button className="tasks-btn primary" onClick={handleSave} disabled={saving || !form.name}>
                {saving ? 'Сохранение…' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
